import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

// Mirrors Joe Masilotti's canonical bridge button:
// https://github.com/joemasilotti/bridge-components/blob/main/web/controllers/button_controller.js
//
// Conventions follow upstream exactly:
//   • Native HTML `title` attribute drives the bar button label (NOT a
//     `data-bridge-title` attribute).
//   • Side via `data-bridge-side` ("left" | "right", default "right"),
//     dispatched as the message event so the native side can route to the
//     correct nav-item slot.
//   • SF Symbol via `data-bridge-ios-image` (Android: `data-bridge-android-image`).
//   • Color via `data-bridge-color` (hex or named).
//   • Disconnect event removes the button.
//
// FloosYo extension:
//   • `data-bridge-badge-count` — integer; renders as an iOS 26+ bar item badge.
//   • `disabled` — when the host element is `disabled` (e.g. a form-dirty
//     gated submit), the native bar item is greyed and unresponsive. A
//     MutationObserver re-sends the item whenever `disabled` toggles, so the
//     button enables the moment the form becomes dirty.
//   • `hide()` / `show()` — take the bar item away while the host is busy
//     (the Banks "+" while Plaid opens and imports, so a second tap has
//     nothing to land on) and put it back. The same messages as leaving and
//     entering the page, so the native side needs nothing new.
export default class extends BridgeComponent {
  static component = "button"
  // Optional reactive badge count. When set (data-bridge--button-badge-count-value),
  // it overrides the static `data-bridge-badge-count` attribute and re-sends the
  // bar item to native whenever it changes — lets the badge update live without
  // re-rendering (and tearing down) the button. Used by the expenses filter.
  static values = { badgeCount: Number }

  #disabledObserver = null
  // While hidden, nothing re-sends the item: the host's `disabled` flips
  // while it is busy, and that observer must not bring the button back.
  #hidden = false

  connect() {
    super.connect()
    this.#addButton()
    this.#observeDisabled()
  }

  badgeCountValueChanged(_current, previous) {
    if (previous === undefined) return // initial connect already sent it
    this.#addButton()
  }

  disconnect() {
    super.disconnect()
    this.#disabledObserver?.disconnect()
    this.#disabledObserver = null
    this.#removeButton()
  }

  // FloosYo ext: dim/undim the native nav bar behind an in-page bottom sheet
  // (the expenses filter), mirroring the FAB voice modal. Called by the
  // expense-filter controller when its sheet opens / closes.
  dim() {
    this.send("dim", {})
  }

  undim() {
    this.send("undim", {})
  }

  hide() {
    if (this.#hidden) return
    this.#hidden = true
    this.#removeButton()
  }

  show() {
    if (!this.#hidden) return
    this.#hidden = false
    this.#addButton()
  }

  #addButton() {
    if (this.#hidden) return
    const el = this.bridgeElement
    const side = el.bridgeAttribute("side") || "right"
    const iosImage = el.bridgeAttribute("ios-image")
    const androidImage = el.bridgeAttribute("android-image")
    const color = el.bridgeAttribute("color")
    let badgeCount
    if (this.hasBadgeCountValue) {
      badgeCount = this.badgeCountValue
    } else {
      const badgeCountRaw = el.bridgeAttribute("badge-count")
      badgeCount = badgeCountRaw == null ? null : parseInt(badgeCountRaw, 10)
    }

    const disabled = this.element.disabled === true

    const data = { title: this.element.title, iosImage, androidImage, color, badgeCount, disabled }

    this.send(side, data, () => {
      if (this.element.disabled === true) return
      this.element.click()
    })
  }

  // Re-send the bar item whenever the host element's `disabled` attribute
  // flips (form-dirty toggles it), so native enables/greys in step.
  #observeDisabled() {
    this.#disabledObserver = new MutationObserver(() => this.#addButton())
    this.#disabledObserver.observe(this.element, {
      attributes: true,
      attributeFilter: ["disabled"]
    })
  }

  #removeButton() {
    const side = this.bridgeElement.bridgeAttribute("side") || "right"
    this.send("disconnect", { side })
  }
}
